import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CloseOutlined, DownOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons';
import cccApi from '../api/cccApi';
import { ALL_WORKSPACE_SUBSTAGES, subsectionLabel } from '../config/recuperacaoWorkspace';
import '@crm/components/CallCenter/Monitoring/Monitoring.css';
import '../styles/recuperacaoInvestigate.css';

type ClaimStatusFilter = 'all' | 'available' | 'claimed';

function formatCccStartedBy(row: Pick<RecuperacaoInvestigateRow, 'is_available' | 'ccc_claimed_by_name' | 'ccc_claimed_by'>): string {
  if (row.is_available) {
    return 'Pool (ninguém iniciou)';
  }
  return row.ccc_claimed_by_name?.trim() || (row.ccc_claimed_by ? `Utilizador #${row.ccc_claimed_by}` : '—');
}

function workspaceOriginLabel(row: RecuperacaoInvestigateRow): string {
  return row.workspace === 'prospeccao' ? 'Prospeção' : 'Recuperação';
}

export interface RecuperacaoInvestigateRow {
  workspace?: 'recuperacao' | 'prospeccao';
  id: number;
  lead_id?: string;
  marcacao_id: string;
  created_at: string | null;
  event_date: string | null;
  nome_empresa?: string;
  nome_responsavel?: string;
  contacto_cliente?: string;
  nif?: string | null;
  produto?: string;
  ccc_claimed_by?: number | null;
  ccc_claimed_by_name?: string | null;
  ccc_substage?: string | null;
  ccc_substage_label?: string | null;
  comercial_gestao_label?: string | null;
  estado_anterior_cliente?: string | null;
  estado_proposta_label?: string | null;
  is_available?: boolean;
  days_in_recuperacao?: number;
}

interface InvestigateFilters {
  nome_empresa: string;
  nif: string;
  morada: string;
  codigo_postal: string;
  contacto_cliente: string;
  nome_responsavel: string;
  ccc_user_id: string;
  claim_status: ClaimStatusFilter;
  ccc_substage: string;
  produtos: string[];
  event_start_date: string;
  event_end_date: string;
  created_start_date: string;
  created_end_date: string;
}

function hasInvestigateSearchCriteria(f: InvestigateFilters): boolean {
  if (f.nome_empresa.trim()) return true;
  if (f.nif.trim()) return true;
  if (f.contacto_cliente.trim()) return true;
  if (f.nome_responsavel.trim()) return true;
  if (f.morada.trim()) return true;
  if (f.codigo_postal.trim()) return true;
  if (f.ccc_user_id.trim()) return true;
  if (f.claim_status !== 'all') return true;
  if (f.ccc_substage.trim()) return true;
  if (f.produtos.length > 0) return true;
  if (f.event_start_date.trim() || f.event_end_date.trim()) return true;
  if (f.created_start_date.trim() || f.created_end_date.trim()) return true;
  return false;
}

interface OptionUser {
  id: string;
  name: string;
}

const EMPTY_FILTERS: InvestigateFilters = {
  nome_empresa: '',
  nif: '',
  morada: '',
  codigo_postal: '',
  contacto_cliente: '',
  nome_responsavel: '',
  ccc_user_id: '',
  claim_status: 'all',
  ccc_substage: '',
  produtos: [],
  event_start_date: '',
  event_end_date: '',
  created_start_date: '',
  created_end_date: '',
};

const PRODUCTS = [
  { value: 'eletricidade', label: 'Eletricidade', color: '#f59e0b' },
  { value: 'gas', label: 'Gás', color: '#10b981' },
  { value: 'bairro-solar', label: 'Bairro', color: '#f97316' },
  { value: 'pap', label: 'PAP', color: '#06b6d4' },
];

const PRODUTO_LABELS: Record<string, string> = {
  eletricidade: 'Eletricidade',
  gas: 'Gás',
  'bairro-solar': 'Bairro Solar',
  pap: 'PAP',
};

const RESULTS_PAGE_SIZE = 10;
const INVESTIGATE_RESULTS_LIMIT = 50;

const fmtDateTime = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtDate = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="iv-field">
    <label className="iv-field__label">{label}</label>
    {children}
  </div>
);

const DateRange: React.FC<{
  label: string;
  from: string;
  to: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}> = ({ label, from, to, onFrom, onTo }) => (
  <div className="iv-field">
    <label className="iv-field__label">{label}</label>
    <div className="iv-date-range">
      <span className="iv-date-range__tag">De</span>
      <input className="iv-input" type="date" value={from} onChange={(e) => onFrom(e.target.value)} />
      <span className="iv-date-range__tag">Até</span>
      <input className="iv-input" type="date" value={to} onChange={(e) => onTo(e.target.value)} />
    </div>
  </div>
);

export type RecuperacaoInvestigateQuickSearchSeed = {
  nome_empresa: string;
  contacto_cliente: string;
  nome_responsavel?: string;
};

export interface RecuperacaoInvestigateModalProps {
  open: boolean;
  onClose: () => void;
  onOpenMarcacao: (row: RecuperacaoInvestigateRow) => void;
  quickSearchSeed?: RecuperacaoInvestigateQuickSearchSeed | null;
}

const RecuperacaoInvestigateModal: React.FC<RecuperacaoInvestigateModalProps> = ({
  open,
  onClose,
  onOpenMarcacao,
  quickSearchSeed = null,
}) => {
  const [filters, setFilters] = useState<InvestigateFilters>(EMPTY_FILTERS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<RecuperacaoInvestigateRow[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [cccUsers, setCccUsers] = useState<OptionUser[]>([]);
  const [selectedRow, setSelectedRow] = useState<RecuperacaoInvestigateRow | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [resultsPage, setResultsPage] = useState(1);

  const set = <K extends keyof InvestigateFilters>(key: K, value: InvestigateFilters[K]) =>
    setFilters((p) => ({ ...p, [key]: value }));

  const toggleProduct = (val: string) =>
    setFilters((p) => ({
      ...p,
      produtos: p.produtos.includes(val) ? p.produtos.filter((x) => x !== val) : [...p.produtos, val],
    }));

  const loadUserOptions = useCallback(async () => {
    try {
      const cccRes = await cccApi.getCccDashboardUsers();
      if (cccRes.success) {
        setCccUsers(
          (cccRes.users || [])
            .map((u) => ({
              id: String(u.id),
              name: String(u.name || u.email || `Utilizador ${u.id}`),
            }))
            .sort((a, b) => a.name.localeCompare(b.name, 'pt'))
        );
      }
    } catch {
      /* soft fail */
    }
  }, []);

  useEffect(() => {
    if (open) void loadUserOptions();
  }, [open, loadUserOptions]);

  const performSearch = useCallback(async (searchFilters: InvestigateFilters) => {
    if (!hasInvestigateSearchCriteria(searchFilters)) {
      setError('Indique pelo menos um critério de pesquisa.');
      setHasSearched(false);
      setRows([]);
      setSelectedRow(null);
      return;
    }
    setLoading(true);
    setError(null);
    setHasSearched(true);
    setResultsPage(1);
    setSelectedRow(null);
    try {
      const res = await cccApi.searchRecuperacaoInvestigateMarcacoes({
        ...searchFilters,
        limit: INVESTIGATE_RESULTS_LIMIT,
      });
      setRows(res.data || []);
    } catch (e: unknown) {
      setRows([]);
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err?.response?.data?.message || err?.message || 'Erro ao pesquisar marcações');
    } finally {
      setLoading(false);
    }
  }, []);

  const runSearch = useCallback(() => {
    void performSearch(filters);
  }, [filters, performSearch]);

  const canSearch = useMemo(() => hasInvestigateSearchCriteria(filters), [filters]);

  const activeCount = useMemo(() => {
    let n = 0;
    (Object.entries(filters) as [keyof InvestigateFilters, unknown][]).forEach(([k, v]) => {
      if (k === 'claim_status' && v !== 'all') {
        n++;
        return;
      }
      if (k === 'ccc_substage' && typeof v === 'string' && v.trim()) {
        n++;
        return;
      }
      if (Array.isArray(v) && v.length) {
        n++;
        return;
      }
      if (typeof v === 'string' && v.trim()) n++;
    });
    return n;
  }, [filters]);

  useEffect(() => {
    if (!open) {
      setFilters(EMPTY_FILTERS);
      setRows([]);
      setHasSearched(false);
      setSelectedRow(null);
      setError(null);
      setResultsPage(1);
      return;
    }

    if (quickSearchSeed) {
      const seeded: InvestigateFilters = {
        ...EMPTY_FILTERS,
        nome_empresa: quickSearchSeed.nome_empresa.trim(),
        contacto_cliente: quickSearchSeed.contacto_cliente.trim(),
        nome_responsavel: (quickSearchSeed.nome_responsavel || '').trim(),
      };
      setFilters(seeded);
      setFiltersOpen(true);
      void performSearch(seeded);
      return;
    }

    setFilters(EMPTY_FILTERS);
    setRows([]);
    setHasSearched(false);
    setSelectedRow(null);
    setError(null);
    setResultsPage(1);
  }, [open, quickSearchSeed, performSearch]);

  const resultsTotalPages = Math.max(1, Math.ceil(rows.length / RESULTS_PAGE_SIZE));
  const paginatedRows = useMemo(() => {
    const start = (resultsPage - 1) * RESULTS_PAGE_SIZE;
    return rows.slice(start, start + RESULTS_PAGE_SIZE);
  }, [rows, resultsPage]);

  useEffect(() => {
    if (resultsPage > resultsTotalPages) {
      setResultsPage(resultsTotalPages);
    }
  }, [resultsPage, resultsTotalPages]);

  if (!open) return null;

  return (
    <div className="iv-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Investigar Recuperação">
      <div className="iv-modal iv-modal--recuperacao" onClick={(e) => e.stopPropagation()}>
        <header className="iv-header">
          <div className="iv-header__left">
            <SearchOutlined className="iv-header__icon" />
            <div>
              <h2 className="iv-header__title">Investigar Marcações</h2>
              <p className="iv-rec-subtitle">Recuperação e Prospeção — pesquisa avançada</p>
            </div>
          </div>
          <div className="iv-header__actions">
            <button
              type="button"
              className="iv-btn iv-btn--primary iv-btn--sm"
              onClick={() => void runSearch()}
              disabled={loading || !canSearch}
            >
              {loading ? 'A pesquisar…' : 'Pesquisar'}
            </button>
            <button
              type="button"
              className="iv-btn iv-btn--ghost iv-btn--sm"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? <UpOutlined /> : <DownOutlined />}
              {filtersOpen ? 'Ocultar critérios' : 'Mostrar critérios'}
              {activeCount > 0 && !filtersOpen ? (
                <span className="iv-header__filter-badge">{activeCount}</span>
              ) : null}
            </button>
            <button type="button" className="iv-close" onClick={onClose} aria-label="Fechar">
              <CloseOutlined />
            </button>
          </div>
        </header>

        <section className={`iv-filters${filtersOpen ? '' : ' iv-filters--collapsed'}`}>
          <div className="iv-row iv-row--5">
            <Field label="Nome da Empresa">
              <input
                className="iv-input"
                placeholder="CallCenter ou Comercial"
                value={filters.nome_empresa}
                onChange={(e) => set('nome_empresa', e.target.value)}
              />
            </Field>
            <Field label="NIF">
              <input
                className="iv-input"
                placeholder="Ex.: 123456789"
                value={filters.nif}
                onChange={(e) => set('nif', e.target.value)}
              />
            </Field>
            <Field label="Contacto">
              <input
                className="iv-input"
                placeholder="Telefone"
                value={filters.contacto_cliente}
                onChange={(e) => set('contacto_cliente', e.target.value)}
              />
            </Field>
            <Field label="Responsável">
              <input
                className="iv-input"
                placeholder="Nome"
                value={filters.nome_responsavel}
                onChange={(e) => set('nome_responsavel', e.target.value)}
              />
            </Field>
            <Field label="Morada">
              <input
                className="iv-input"
                placeholder="Rua, localidade…"
                value={filters.morada}
                onChange={(e) => set('morada', e.target.value)}
              />
            </Field>
          </div>

          <div className="iv-row iv-row--products">
            <Field label="Código Postal">
              <input
                className="iv-input"
                placeholder="Ex.: 1000-123"
                value={filters.codigo_postal}
                onChange={(e) => set('codigo_postal', e.target.value)}
              />
            </Field>
            <Field label="Iniciado por (CCC)">
              <select
                className="iv-input iv-select"
                value={filters.ccc_user_id}
                onChange={(e) => set('ccc_user_id', e.target.value)}
              >
                <option value="">Todos</option>
                {cccUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Localização">
              <select
                className="iv-input iv-select"
                value={filters.claim_status}
                onChange={(e) => set('claim_status', e.target.value as ClaimStatusFilter)}
              >
                <option value="all">Pool + Área de trabalho</option>
                <option value="available">Só na Pool</option>
                <option value="claimed">Só na área de trabalho</option>
              </select>
            </Field>
            <Field label="Estado CCC">
              <select
                className="iv-input iv-select"
                value={filters.ccc_substage}
                onChange={(e) => set('ccc_substage', e.target.value)}
              >
                <option value="">Todos</option>
                {ALL_WORKSPACE_SUBSTAGES.map((st) => (
                  <option key={st} value={st}>
                    {subsectionLabel(st)}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="iv-row iv-row--products">
            <Field label="Produto">
              <div className="iv-product-pills">
                {PRODUCTS.map((p) => {
                  const on = filters.produtos.includes(p.value);
                  return (
                    <button
                      key={p.value}
                      type="button"
                      className={`iv-pill${on ? ' iv-pill--on' : ''}`}
                      style={on ? { backgroundColor: p.color, borderColor: p.color } : {}}
                      onClick={() => toggleProduct(p.value)}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>

          <div className="iv-row iv-row--dates">
            <DateRange
              label="Data da Marcação"
              from={filters.event_start_date}
              to={filters.event_end_date}
              onFrom={(v) => set('event_start_date', v)}
              onTo={(v) => set('event_end_date', v)}
            />
            <DateRange
              label="Data de Criação"
              from={filters.created_start_date}
              to={filters.created_end_date}
              onFrom={(v) => set('created_start_date', v)}
              onTo={(v) => set('created_end_date', v)}
            />
          </div>

          <div className="iv-actions">
            {error && <span className="iv-actions__error">{error}</span>}
            <span className="iv-actions__count">
              {activeCount > 0
                ? `${activeCount} filtro${activeCount !== 1 ? 's' : ''} ativo${activeCount !== 1 ? 's' : ''}`
                : 'Sem filtros ativos'}
            </span>
            <button
              type="button"
              className="iv-btn iv-btn--ghost"
              onClick={() => {
                setFilters(EMPTY_FILTERS);
                setRows([]);
                setHasSearched(false);
                setResultsPage(1);
                setSelectedRow(null);
              }}
            >
              Limpar
            </button>
            <button
              type="button"
              className="iv-btn iv-btn--primary"
              onClick={() => void runSearch()}
              disabled={loading || !canSearch}
            >
              {loading ? 'A pesquisar…' : 'Pesquisar'}
            </button>
          </div>
        </section>

        <div className="iv-body">
          <div className="iv-pane iv-pane--results">
            <div className="iv-pane__head">
              <h3>Resultados</h3>
              {hasSearched && (
                <span className="iv-pane__count">
                  {rows.length} marcaç{rows.length !== 1 ? 'ões' : 'ão'}
                </span>
              )}
            </div>
            <div className="iv-results-body">
              <div className="iv-table-wrap">
                {!hasSearched ? (
                  <div className="iv-empty">
                    <SearchOutlined className="iv-empty__icon" />
                    <p>
                      Defina pelo menos um critério (empresa, NIF, contacto, datas, etc.) e clique em{' '}
                      <strong>Pesquisar</strong>.
                    </p>
                  </div>
                ) : rows.length === 0 ? (
                  <div className="iv-empty">
                    <p>Nenhum resultado em Recuperação ou Prospeção corresponde aos critérios.</p>
                  </div>
                ) : (
                  <table className="iv-table">
                    <thead>
                      <tr>
                        <th>Área</th>
                        <th>Empresa</th>
                        <th>NIF</th>
                        <th>Contacto</th>
                        <th>Local</th>
                        <th>Estado CCC</th>
                        <th>Iniciado por (CCC)</th>
                        <th>Dias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRows.map((row, idx) => (
                        <tr
                          key={`${row.marcacao_id}-${(resultsPage - 1) * RESULTS_PAGE_SIZE + idx}`}
                          className={[
                            'iv-table__row',
                            idx % 2 === 1 ? 'iv-table__row--odd' : '',
                            selectedRow?.marcacao_id === row.marcacao_id ? 'iv-table__row--selected' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={() => setSelectedRow(row)}
                        >
                          <td>
                            <span
                              className={`iv-rec-pill ${
                                row.workspace === 'prospeccao' ? 'iv-rec-pill--prosp' : 'iv-rec-pill--rec'
                              }`}
                            >
                              {workspaceOriginLabel(row)}
                            </span>
                          </td>
                          <td>
                            <strong>{row.nome_empresa || '—'}</strong>
                            <div className="iv-rec-cell-sub">{row.marcacao_id}</div>
                          </td>
                          <td>{row.nif || '—'}</td>
                          <td>{row.contacto_cliente || '—'}</td>
                          <td>
                            {row.workspace === 'prospeccao' ? (
                              <span className="iv-rec-pill iv-rec-pill--ws">Área de trabalho</span>
                            ) : row.is_available ? (
                              <span className="iv-rec-pill iv-rec-pill--pool">Pool</span>
                            ) : (
                              <span className="iv-rec-pill iv-rec-pill--ws">Área de trabalho</span>
                            )}
                          </td>
                          <td>{row.ccc_substage_label || (row.is_available ? '—' : 'Iniciado')}</td>
                          <td>{formatCccStartedBy(row)}</td>
                          <td>{row.days_in_recuperacao ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {hasSearched && rows.length > 0 ? (
                <div className="iv-pagination">
                  <button
                    type="button"
                    className="iv-pagination__btn"
                    onClick={() => setResultsPage((p) => Math.max(1, p - 1))}
                    disabled={resultsPage === 1}
                  >
                    Anterior
                  </button>
                  <span className="iv-pagination__info">
                    Página {resultsPage} de {resultsTotalPages} ·{' '}
                    {(resultsPage - 1) * RESULTS_PAGE_SIZE + 1}–
                    {Math.min(resultsPage * RESULTS_PAGE_SIZE, rows.length)} de {rows.length}
                  </span>
                  <button
                    type="button"
                    className="iv-pagination__btn"
                    onClick={() => setResultsPage((p) => Math.min(resultsTotalPages, p + 1))}
                    disabled={resultsPage >= resultsTotalPages}
                  >
                    Seguinte
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <div className="iv-pane iv-pane--rec-detail">
            <div className="iv-pane__head">
              <h3>Detalhe</h3>
              {selectedRow ? (
                <span className="iv-pane__selection">{selectedRow.marcacao_id}</span>
              ) : (
                <span className="iv-pane__hint">Selecione uma marcação nos resultados</span>
              )}
            </div>
            <div className="iv-rec-detail">
              {!selectedRow ? (
                <div className="iv-empty iv-empty--compact">
                  <p>Os detalhes da marcação aparecem aqui.</p>
                </div>
              ) : (
                <>
                  <dl className="iv-rec-detail-grid">
                    <div>
                      <dt>Área</dt>
                      <dd>{workspaceOriginLabel(selectedRow)}</dd>
                    </div>
                    <div>
                      <dt>Empresa</dt>
                      <dd>{selectedRow.nome_empresa || '—'}</dd>
                    </div>
                    <div>
                      <dt>Responsável</dt>
                      <dd>{selectedRow.nome_responsavel || '—'}</dd>
                    </div>
                    <div>
                      <dt>NIF</dt>
                      <dd>{selectedRow.nif || '—'}</dd>
                    </div>
                    <div>
                      <dt>Contacto</dt>
                      <dd>{selectedRow.contacto_cliente || '—'}</dd>
                    </div>
                    <div>
                      <dt>Produto</dt>
                      <dd>
                        {selectedRow.produto
                          ? PRODUTO_LABELS[selectedRow.produto] || selectedRow.produto
                          : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt>Data marcação</dt>
                      <dd>{fmtDate(selectedRow.event_date)}</dd>
                    </div>
                    <div>
                      <dt>Criada em</dt>
                      <dd>{fmtDateTime(selectedRow.created_at)}</dd>
                    </div>
                    <div>
                      <dt>Dias em Recuperação</dt>
                      <dd>
                        {selectedRow.workspace === 'prospeccao'
                          ? '—'
                          : selectedRow.days_in_recuperacao ?? '—'}
                      </dd>
                    </div>
                    <div>
                      <dt>Localização</dt>
                      <dd>
                        {selectedRow.workspace === 'prospeccao'
                          ? 'Área de trabalho Prospeção'
                          : selectedRow.is_available
                            ? 'Pool (disponível)'
                            : 'Área de trabalho CCC'}
                      </dd>
                    </div>
                    <div>
                      <dt>Iniciado por (CCC)</dt>
                      <dd>{formatCccStartedBy(selectedRow)}</dd>
                    </div>
                    <div>
                      <dt>Estado CCC</dt>
                      <dd>{selectedRow.ccc_substage_label || '—'}</dd>
                    </div>
                    <div>
                      <dt>Estado Comercial (Gestão)</dt>
                      <dd>{selectedRow.comercial_gestao_label || '—'}</dd>
                    </div>
                    <div>
                      <dt>Estado anterior do cliente</dt>
                      <dd>{selectedRow.estado_anterior_cliente || '—'}</dd>
                    </div>
                    <div>
                      <dt>Estado proposta</dt>
                      <dd>{selectedRow.estado_proposta_label || '—'}</dd>
                    </div>
                  </dl>
                  <button
                    type="button"
                    className="iv-btn iv-btn--primary iv-rec-open-btn"
                    onClick={() => onOpenMarcacao(selectedRow)}
                  >
                    {selectedRow.workspace === 'prospeccao'
                      ? 'Abrir na Prospeção'
                      : 'Abrir na Recuperação'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecuperacaoInvestigateModal;
